import { OrientationType } from "@lib/titan2/dist/game/renderer/constant/RendererConstants";
import { EventTimeline, Skin } from "@pixi-spine/all-4.0";
export class GameSpineUtil {
    /**
     * Add a display object to a specific slot container.
     */
    static addToSlot(spine, displayObject, slotName) {
        const slot = this.getSlot(spine.getSpine(), slotName);
        if (slot) {
            slot.addChild(displayObject);
        }
        else {
            console.warn(`addToSpineSlot - Slot not found: ` + slotName);
        }
    }
    /**
     * Get the slot container for a given slot name.
     */
    static getSlot(spine, slotName) {
        const slotIndex = spine.skeleton.findSlotIndex(slotName);
        return spine.slotContainers?.[slotIndex] ?? null;
    }
    /**
     * Set a skin by name, or set a combined skin from multiple names.
     */
    static setSkin(spine, ...skins) {
        for (const orientation of this.orientations(spine)) {
            const skin = this.getSkin(orientation, ...skins);
            orientation.skeleton.setSkin(skin);
            orientation.skeleton.setToSetupPose();
        }
    }
    /**
     * Get a skin by name, or get a combined skin from multiple names.
     */
    static getSkin(spine, ...skins) {
        if (skins.length > 1) {
            const combinedSkin = new Skin(skins.join("_"));
            for (const skinName of skins) {
                const skin = spine.spineData.findSkin(skinName);
                if (!skin) {
                    throw new Error(`Missing skin: ${skinName}`);
                }
                combinedSkin.addSkin(skin);
            }
            return combinedSkin;
        }
        const skinName = skins[0];
        const skin = spine.spineData.findSkin(skinName);
        if (!skin) {
            throw new Error(`Missing skin: ${skinName}`);
        }
        return skin;
    }
    /**
     * Show the last frame of a spine animation.
     */
    static gotoLastFrameAndStop(spine, animation, track = 0, dispatchEvents = false) {
        for (const orientation of this.orientations(spine)) {
            const entry = orientation.state.setAnimation(track, animation, false);
            entry.trackTime = entry.animation?.duration ?? 0;
            if (!dispatchEvents) {
                entry.setAnimationLast(entry.trackTime);
            }
            orientation.update(0);
        }
    }
    /**
     * Enumerate the orientations of a TitanSpine.
     */
    static *orientations(spine) {
        const landscape = spine.getSpine(OrientationType.LANDSCAPE);
        if (landscape) {
            yield landscape;
        }
        const portrait = spine.getSpine(OrientationType.PORTRAIT);
        if (portrait) {
            yield portrait;
        }
    }
    static getEventTime(spine, eventName, track = 0) {
        const trackEntry = spine.getTrackEntry(track);
        if (!trackEntry) {
            console.warn(`Track entry not found for track ${track}.`);
            return -1;
        }
        const eventTimeline = trackEntry.animation?.timelines?.find(t => t instanceof EventTimeline);
        if (!eventTimeline) {
            console.warn(`Event timeline not found for animation "${trackEntry.animation.name}".`);
            return -1;
        }
        const event = eventTimeline.events.find(e => e.data.name === eventName);
        if (!event) {
            console.warn(`Event "${eventName}" not found in animation "${trackEntry.animation.name}".`);
            return -1;
        }
        return event.time;
    }
    static bindTextToSlot(spine, slotName, text) {
        const pixiText = text.getPixiText();
        pixiText.texture.on("update", () => this.padTextureInSlot(spine, slotName, pixiText.texture));
        pixiText.dirty = true;
        pixiText.renderable = false;
        this.replaceTextureInSlot(spine, slotName, pixiText.texture);
    }
    static replaceTextureInSlot(spine, slotName, texture) {
        for (const orientation of this.orientations(spine)) {
            orientation.hackTextureBySlotName(slotName, texture);
        }
    }
    static padTextureInSlot(spine, slotName, texture) {
        const slot = spine.findSlot(slotName);
        const originalSlotTexture = slot.getAttachment().region.texture;
        const originalAspect = originalSlotTexture.width / originalSlotTexture.height;
        const actualAspect = texture.frame.width / texture.frame.height;
        if (actualAspect > originalAspect) {
            const paddedHeight = texture.frame.width / originalAspect;
            const paddedY = (texture.frame.height - paddedHeight) / 2;
            texture.frame.y = paddedY;
            texture.frame.height = paddedHeight;
        }
        else {
            const paddedWidth = texture.frame.height * originalAspect;
            const paddedX = (texture.frame.width - paddedWidth) / 2;
            texture.frame.x = paddedX;
            texture.frame.width = paddedWidth;
        }
    }
    /**
     * Fixes a bug from Titan's SpineUtil which compares animation string length instead of found animations
     */
    static getDuration(spine, animation) {
        const spineData = spine.getSpine()?.spineData;
        if (spineData) {
            const animNames = Array.isArray(animation)
                ? animation.slice()
                : [animation];
            const animations = spineData.animations.filter((anim) => animNames.indexOf(anim.name) > -1);
            if (animations.length > 1) {
                return animations.map((anim) => anim.duration);
            }
            else if (animations.length === 1) { //line changed from "animation" -> "animations" fixing the bug
                return animations[0].duration;
            }
        }
        return null;
    }
    static forceUpdate(spine) {
        for (const orientation of this.orientations(spine)) {
            orientation.update(0);
        }
    }
    static setReversed(spine, track, reversed) {
        const trackEntry = spine.getTrackEntry(track);
        if (trackEntry) {
            trackEntry.reverse = reversed;
        }
    }
}
//# sourceMappingURL=GameSpineUtil.js.map